// Feed view — the third per-form view, beside Form layout and Grid layout.
//
// It says how ONE record of this form reads when it appears INSIDE something
// else: a step in a process trail, a record behind a process-diagram node. A
// trail card is not a grid row and not a form — it has to say what this
// document is, when it happened, whether it went through, what it says, and
// what was on it.
//
// The six wells ARE the Feeds visual's own (First/Second/Third Row × Left and
// Right, stored as `visualLayout` ColumnGroups), dragged the same way, so a
// designer who has laid out a Feeds visual already knows this screen and one
// renderer serves both. The three below them are what a transaction has and a
// BI row does not: a sentence, a child form's lines, and an icon for the rail.
//
// The columns are dragged out of the OBJECT TREE on the left — the list this
// designer already reads and edits columns from. A bank of its own beside it
// would be the same list printed twice, and two lists that can disagree about
// what a column is called. Each well also has a "+" for the times dragging
// isn't available: the tree collapsed, or a keyboard.
//
// A column that has landed is draggable too — within its row to reorder it,
// across rows to move it — because a card is read left to right and top to
// bottom, so WHERE a value sits is half of what is being authored. The drop
// lands at the insertion line, not on the end of the pile.
//
// Leaving it empty is a real answer: the engine derives a layout from the
// form's own declarations, so a form nobody visits still reads.

import { useCallback, useMemo, useState } from 'react'
import { GripVertical, Pencil, Plus, X } from 'lucide-react'

import type {
  ColumnDef, FeedColumnGroup, FeedColumnRef, FeedViewConfig, SqlModel, TransactionModel,
} from '@twasta/page-runtime/txn/types'
import { FeedCard } from '@twasta/page-runtime/txn/flow'
import type { FeedCardModel, FeedCell } from '@twasta/page-runtime/txn/flow'
import { clearColumnDrag, hasColumnDrag, readColumnDrag } from './columnDrag'

/** The Feeds visual's wells, in render order — same StandardNames the visual
 *  catalog declares (backend/app/data/DashboardVisuals.json). */
const WELLS: Array<{ std: string; name: string; hint: string }> = [
  { std: 'FirstRowLeft', name: 'First row left', hint: 'What this document is' },
  { std: 'FirstRowRight', name: 'First row right', hint: 'When it happened' },
  { std: 'SecondRowLeft', name: 'Second row left', hint: 'Which one it is' },
  { std: 'SecondRowRight', name: 'Second row right', hint: 'What it was worth' },
  { std: 'ThirdRowLeft', name: 'Third row left', hint: 'Whether it went through' },
  { std: 'ThirdRowRight', name: 'Third row right', hint: 'Anything else' },
]

const CARD_KEY: Record<string, keyof FeedCardModel['rows']> = {
  FirstRowLeft: 'first_left',
  FirstRowRight: 'first_right',
  SecondRowLeft: 'second_left',
  SecondRowRight: 'second_right',
  ThirdRowLeft: 'third_left',
  ThirdRowRight: 'third_right',
}

interface Props {
  model: TransactionModel
  form: SqlModel
  formIndex: number
  onModelChange: (next: TransactionModel) => void
}

export default function FeedViewEditor({ model, form, formIndex, onModelChange }: Props) {
  /** Where the drop would land: the well, and the slot within it. Held as the
   *  insertion INDEX rather than a hovered chip, so the line drawn on screen
   *  and the splice that follows are the same number. */
  const [over, setOver] = useState<{ std: string; at: number } | null>(null)
  const [adding, setAdding] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  /** The cell whose heading is being typed, and the text so far. */
  const [labelling, setLabelling] = useState<{ std: string; idx: number } | null>(null)
  const [draft, setDraft] = useState('')

  const fv: FeedViewConfig = form.feed_view || {}
  const columns = useMemo(
    () => (form.columns || []).map((c) => c.column).filter(Boolean),
    [form.columns])
  const byDb = useMemo(() => {
    const m = new Map<string, ColumnDef>()
    for (const c of columns) if (c.db_name) m.set(c.db_name.toUpperCase(), c)
    return m
  }, [columns])

  const groups = useMemo(() => {
    const out = new Map<string, FeedColumnGroup>()
    for (const w of WELLS) {
      out.set(w.std, { GroupName: w.name, StandardName: w.std, COLUMNS: [] })
    }
    for (const g of fv.visualLayout || []) {
      const hit = out.get(g.StandardName)
      if (hit) hit.COLUMNS = [...(g.COLUMNS || [])]
    }
    return out
  }, [fv.visualLayout])

  const patch = useCallback((next: Partial<FeedViewConfig>) => {
    const forms = [...model.sql_models]
    const merged: FeedViewConfig = { ...(forms[formIndex].sql_model.feed_view || {}), ...next }
    // An emptied feed view is REMOVED, not stored blank — that is what puts the
    // form back on the derived default instead of pinning it to nothing.
    const empty = !(merged.visualLayout || []).some((g) => (g.COLUMNS || []).length)
      && !merged.narrative && !merged.icon && !merged.detail
    forms[formIndex] = {
      sql_model: {
        ...forms[formIndex].sql_model,
        ...(empty ? { feed_view: undefined } : { feed_view: merged }),
      },
    }
    onModelChange({ ...model, sql_models: forms })
  }, [model, formIndex, onModelChange])

  const writeGroups = useCallback((mutate: (g: Map<string, FeedColumnGroup>) => void) => {
    const next = new Map(groups)
    for (const [k, v] of next) next.set(k, { ...v, COLUMNS: [...(v.COLUMNS || [])] })
    mutate(next)
    patch({ visualLayout: WELLS.map((w) => next.get(w.std)!).filter((g) => g.COLUMNS.length) })
  }, [groups, patch])

  /** `at` is the slot to land in; omitted, the column goes on the end. */
  const place = useCallback((ref: string, std: string, at?: number) => {
    writeGroups((g) => {
      // A column lives in one well at a time — dragging it moves it.
      let slot = at
      for (const [key, grp] of g) {
        const was = grp.COLUMNS.findIndex((c) => cellRef(c) === ref)
        if (was < 0) continue
        grp.COLUMNS.splice(was, 1)
        // Taking it out ahead of the target slot shifts that slot down one.
        if (key === std && slot != null && was < slot) slot -= 1
      }
      const target = g.get(std)
      if (!target) return
      const cell: FeedColumnRef = { name: ref }
      target.COLUMNS.splice(clamp(slot ?? target.COLUMNS.length, target.COLUMNS.length), 0, cell)
    })
  }, [writeGroups])

  /** Move a cell that is already placed — the reorder, and the row-to-row
   *  move, are the same operation with a different destination. */
  const moveCell = useCallback((from: { std: string; idx: number }, std: string, at: number) => {
    writeGroups((g) => {
      const src = g.get(from.std)
      const dst = g.get(std)
      if (!src || !dst) return
      const [cell] = src.COLUMNS.splice(from.idx, 1)
      if (!cell) return
      const slot = from.std === std && from.idx < at ? at - 1 : at
      dst.COLUMNS.splice(clamp(slot, dst.COLUMNS.length), 0, cell)
    })
  }, [writeGroups])

  /** One drop handler for both drags: a column arriving from the object tree,
   *  and a cell already on the card being rearranged. `at` is the slot the
   *  insertion line was drawn at; omitted, the drop was on the well itself and
   *  lands at the end. */
  const handleDrop = useCallback((e: React.DragEvent, std: string, at?: number) => {
    e.preventDefault()
    e.stopPropagation()
    setOver(null)

    const moving = readCellDrag(e)
    const payload = moving ? null : readColumnDrag(e)
    clearCellDrag()
    clearColumnDrag()

    if (moving) {
      setProblem(null)
      moveCell(moving, std, at ?? (groups.get(std)?.COLUMNS.length ?? 0))
      return
    }
    if (!payload) return
    // The tree lists every form's columns; a card describes ONE record.
    if (payload.formIndex !== formIndex) {
      const other = model.sql_models?.[payload.formIndex]?.sql_model
      const where = other?.form_title ? `“${other.form_title}”` : `form ${payload.formIndex + 1}`
      setProblem(
        `“${payload.label || payload.name}” belongs to ${where}. A feed card is laid `
        + `out from this form’s own columns — to show another form’s rows, use `
        + `Detail lines below.`)
      return
    }
    if (!byDb.has(payload.name.toUpperCase())) {
      setProblem(`“${payload.name}” is no longer a column of this form.`)
      return
    }
    setProblem(null)
    place(payload.name, std, at)
  }, [formIndex, model.sql_models, byDb, place, moveCell, groups])

  const dragging = useCallback((e: React.DragEvent) => hasColumnDrag(e) || hasCellDrag(e), [])

  /** A caption — words of the author's own, not a value. Seeded with the form's
   *  title because that is what most of them say, and opened for typing right
   *  away because the whole point is that the words are theirs. */
  const addCaption = useCallback((std: string) => {
    const at = groups.get(std)?.COLUMNS.length ?? 0
    const seed = form.form_title || 'Caption'
    writeGroups((g) => {
      const target = g.get(std)
      if (!target) return
      target.COLUMNS.push({ constant: seed })
    })
    setDraft(seed)
    setLabelling({ std, idx: at })
  }, [groups, form.form_title, writeGroups])

  /** Retype a caption. Cleared to nothing, it goes: a caption with no words is
   *  not a caption. */
  const setCaption = useCallback((std: string, idx: number, text: string) => {
    writeGroups((g) => {
      const target = g.get(std)
      const cell = target?.COLUMNS[idx]
      if (!target || !cell) return
      const trimmed = text.trim()
      if (!trimmed) target.COLUMNS.splice(idx, 1)
      else target.COLUMNS[idx] = { ...cell, constant: trimmed }
    })
  }, [writeGroups])

  /** The heading printed in front of a value. Blank clears it — an empty
   *  string stored is still a heading as far as the card is concerned. */
  const setLabel = useCallback((std: string, idx: number, text: string) => {
    writeGroups((g) => {
      const target = g.get(std)
      const cell = target?.COLUMNS[idx]
      if (!target || !cell) return
      const trimmed = text.trim()
      const next = { ...cell }
      if (trimmed) next.label = trimmed
      else delete next.label
      target.COLUMNS[idx] = next
    })
  }, [writeGroups])

  const remove = useCallback((std: string, idx: number) => {
    writeGroups((g) => {
      const target = g.get(std)
      if (target) target.COLUMNS = target.COLUMNS.filter((_, i) => i !== idx)
    })
  }, [writeGroups])

  const placed = useMemo(() => {
    const s = new Set<string>()
    for (const [, g] of groups) {
      for (const c of g.COLUMNS) {
        const ref = cellRef(c)
        if (ref) s.add(ref)
      }
    }
    return s
  }, [groups])

  // ── the live preview, through the SAME card the runtime draws ────────────
  const previewCard: FeedCardModel = useMemo(() => {
    const rows: FeedCardModel['rows'] = {}
    for (const w of WELLS) {
      const cells: FeedCell[] = []
      for (const c of groups.get(w.std)?.COLUMNS || []) {
        if (c.constant != null) { cells.push({ text: c.constant }); continue }
        const col = byDb.get((c.name || '').toUpperCase())
        if (col) cells.push({ value: sampleValue(col), col, label: c.label || undefined })
      }
      if (cells.length) rows[CARD_KEY[w.std]] = cells
    }
    return { icon: fv.icon, rows, has_body: !!(fv.narrative || fv.detail) }
  }, [groups, byDb, fv.icon, fv.narrative, fv.detail])

  const childForms = (model.sql_models || [])
    .map((f, i) => ({ sm: f.sql_model, i }))
    .filter(({ sm }) => sm.form_no && sm.form_no !== form.form_no)

  const detailForm = childForms.find(
    ({ sm }) => sm.form_no === fv.detail?.form_no)?.sm

  return (
    // Wrapping, not a fixed pair of columns: this editor sits between two
    // panels the designer can drag, so "is there room for the preview beside
    // the wells" is a question about THIS box, not about the window. Below its
    // basis the preview wraps under the wells rather than being squeezed.
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
      {/* the wells */}
      <div style={{ flex: '999 1 360px', minWidth: 0 }}>
        <div style={{ ...hint, marginTop: 0, marginBottom: 8 }}>
          Drag a column from the object tree on the left into a row — or use
          <Plus size={10} style={{ verticalAlign: -1, margin: '0 2px' }} />
          to choose one. Drag a placed column to reorder it or move it to
          another row. The
          <Plus size={10} style={{ verticalAlign: -1, margin: '0 2px' }} />
          menu also adds a text caption, and
          <Pencil size={10} style={{ verticalAlign: -1, margin: '0 2px' }} />
          on a chip retypes a caption or puts a heading in front of a value. Leave every row empty and the card is built from this
          form’s own key, date, amount and status.
        </div>
        {problem && (
          <div
            role="alert"
            style={{
              ...hint, marginTop: 0, marginBottom: 8, padding: '5px 8px', borderRadius: 6,
              color: 'var(--color-warning, #b45309)',
              border: '1px solid var(--color-warning, #b45309)',
              background: 'var(--color-bg-secondary)',
            }}
          >
            {problem}
          </div>
        )}
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        }}>
          {WELLS.map((w) => {
            const g = groups.get(w.std)!
            const lit = over?.std === w.std
            return (
              <div
                key={w.std}
                onDragOver={(e) => {
                  if (!dragging(e)) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'copy'
                  // Over the well but below its chips: the end of the row.
                  const at = g.COLUMNS.length
                  if (over?.std !== w.std || over?.at !== at) setOver({ std: w.std, at })
                }}
                onDragLeave={(e) => {
                  // Leaving for a child of this well is not leaving the well.
                  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
                  setOver((o) => (o?.std === w.std ? null : o))
                }}
                onDrop={(e) => handleDrop(e, w.std)}
                style={{
                  ...well,
                  borderStyle: lit || g.COLUMNS.length ? 'solid' : 'dashed',
                  borderColor: lit ? 'var(--color-accent)' : 'var(--color-border)',
                  background: lit
                    ? 'var(--color-accent-soft, rgba(37,99,235,0.08))'
                    : 'var(--color-bg)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ ...wellLabel, marginBottom: 0, flex: 1 }}>{w.name}</div>
                  <button
                    type="button"
                    aria-label={`Add a column to ${w.name}`}
                    title={`Add a column to ${w.name}`}
                    onClick={() => { setProblem(null); setAdding(adding === w.std ? null : w.std) }}
                    style={{
                      background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 1,
                      color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center',
                    }}
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <div style={{ height: 5 }} />
                {adding === w.std && (
                  <select
                    autoFocus
                    value=""
                    onChange={(e) => {
                      const pick = e.target.value
                      setAdding(null)
                      if (!pick) return
                      if (pick === TEXT_REF) addCaption(w.std)
                      else place(pick, w.std)
                    }}
                    onBlur={() => setAdding(null)}
                    style={{ ...select, marginBottom: 5 }}
                  >
                    <option value="">Choose a column…</option>
                    <option value={TEXT_REF}>Text caption…</option>
                    {columns
                      .filter((c) => c.db_name && !placed.has(c.db_name))
                      .map((c) => (
                        <option key={c.db_name} value={c.db_name}>
                          {c.db_name}{c.heading ? ` — ${c.heading}` : ''}
                        </option>
                      ))}
                  </select>
                )}
                {g.COLUMNS.length === 0 && adding !== w.std && <div style={hint}>{w.hint}</div>}
                {/* Side by side and wrapping — several columns in one well
                    render as ONE line on the card, so the authoring surface
                    has to look like the line it is building. */}
                {g.COLUMNS.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                    {g.COLUMNS.map((c, i) => (
                      <PlacedChip
                        key={`${cellRef(c)}-${i}`}
                        label={c.constant != null ? `“${c.constant}”` : (c.name || '')}
                        heading={c.label}
                        // A caption's button retypes the caption; a column's
                        // sets the heading printed in front of its value.
                        editHint={c.constant != null
                          ? 'Change this text'
                          : (c.label ? `Heading: ${c.label}` : 'Add a heading')}
                        insertBefore={lit && over?.at === i}
                        editing={labelling?.std === w.std && labelling?.idx === i}
                        draft={draft}
                        onDraft={setDraft}
                        onEdit={() => {
                          setProblem(null)
                          setDraft(c.constant != null ? (c.constant || '') : (c.label || ''))
                          setLabelling({ std: w.std, idx: i })
                        }}
                        onCommit={(text) => {
                          setLabelling(null)
                          const was = (c.constant != null ? c.constant : c.label) || ''
                          if ((text || '').trim() === was.trim()) return
                          if (c.constant != null) setCaption(w.std, i, text)
                          else setLabel(w.std, i, text)
                        }}
                        onCancelEdit={() => setLabelling(null)}
                        onRemove={() => remove(w.std, i)}
                        onDragStart={(e) => setCellDrag(e, { std: w.std, idx: i })}
                        onDragEnd={() => { clearCellDrag(); setOver(null) }}
                        onDragOver={(e) => {
                          if (!dragging(e)) return
                          e.preventDefault()
                          e.stopPropagation()
                          e.dataTransfer.dropEffect = 'copy'
                          // Left of the chip's middle drops before it, right after.
                          const r = e.currentTarget.getBoundingClientRect()
                          const at = e.clientX < r.left + r.width / 2 ? i : i + 1
                          if (over?.std !== w.std || over?.at !== at) setOver({ std: w.std, at })
                        }}
                        onDrop={(e) => handleDrop(e, w.std, over?.std === w.std ? over.at : i)}
                      />
                    ))}
                    {lit && over?.at === g.COLUMNS.length && <InsertLine />}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div style={{ ...well, borderStyle: 'solid', marginTop: 8, borderColor: 'var(--color-info, #0f766e)' }}>
          <div style={{ ...wellLabel, color: 'var(--color-info, #0f766e)' }}>Narrative</div>
          <textarea
            value={fv.narrative || ''}
            onChange={(e) => patch({ narrative: e.target.value })}
            placeholder="Material received from {form.SUPPLIER_NAME} with Challan {form.CHALLAN_NO}…"
            rows={3}
            style={{
              width: '100%', fontSize: 13, fontFamily: 'monospace', padding: '6px 8px',
              borderRadius: 6, border: '1px solid var(--color-border)',
              background: 'var(--color-bg)', color: 'var(--color-text)', resize: 'vertical',
            }}
          />
          <div style={hint}>
            A sentence about the record, shown when the card is opened. Use
            <code style={{ margin: '0 3px' }}>{'{form.COLUMN}'}</code> for a value.
            Left blank, the card simply has no sentence.
          </div>
        </div>

        <div style={{ ...well, borderStyle: 'solid', marginTop: 8, borderColor: 'var(--color-info, #0f766e)' }}>
          <div style={{ ...wellLabel, color: 'var(--color-info, #0f766e)' }}>Detail lines</div>
          <select
            value={fv.detail?.form_no || ''}
            onChange={(e) => patch({
              detail: e.target.value
                ? { form_no: e.target.value, columns: fv.detail?.columns || [], limit: 10 }
                : null,
            })}
            style={select}
          >
            <option value="">None</option>
            {childForms.map(({ sm }) => (
              <option key={sm.form_no} value={sm.form_no}>
                Form {sm.form_no} · {sm.form_title || sm.main_table}
              </option>
            ))}
          </select>
          {detailForm && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
              {(detailForm.columns || []).map((c) => c.column).filter((c) => c?.db_name).map((c) => {
                const on = (fv.detail?.columns || []).includes(c.db_name!)
                return (
                  <button
                    key={c.db_name}
                    type="button"
                    onClick={() => patch({
                      detail: {
                        form_no: fv.detail!.form_no,
                        limit: fv.detail?.limit || 10,
                        columns: on
                          ? (fv.detail?.columns || []).filter((x) => x !== c.db_name)
                          : [...(fv.detail?.columns || []), c.db_name!],
                      },
                    })}
                    style={{
                      ...chipBase,
                      cursor: 'pointer',
                      background: on ? 'var(--color-accent)' : 'var(--color-bg)',
                      color: on ? '#fff' : 'var(--color-text-secondary)',
                      borderColor: on ? 'var(--color-accent)' : 'var(--color-border)',
                    }}
                  >
                    {c.db_name}
                  </button>
                )
              })}
            </div>
          )}
          {!detailForm && <div style={hint}>A child form’s lines, shown as a small table.</div>}
        </div>
      </div>

      {/* the preview */}
      <div style={{ ...panel, flex: '1 1 260px', minWidth: 0, maxWidth: '100%' }}>
        <div style={eyebrow}>Reads as</div>
        <FeedCard card={previewCard} title={form.form_title || 'This form'} />
        <div style={hint}>
          Sample values. The real card formats every value exactly as this
          form’s grid does.
        </div>
      </div>
    </div>
  )
}

/** The "+" menu's entry for a free-text caption. Not a reference to anything —
 *  picking it writes a new caption cell and opens it for typing. */
const TEXT_REF = '__caption__'

/** What a placed cell REFERS to — a column, by name. A caption refers to
 *  nothing: it is its own text, there may be several on a card, and none of
 *  them is "already placed". */
function cellRef(c: FeedColumnRef): string {
  return c.constant != null ? '' : (c.name || '')
}

function clamp(at: number, len: number): number {
  return Math.max(0, Math.min(at, len))
}

// A cell already on the card, being rearranged. Its own flavour, so a well can
// tell "move what is here" from "add what the tree sent".
const CELL_MIME = 'application/x-twasta-feedcell'

interface CellDrag { std: string; idx: number }

// Held here as well as on the event, for the same reason the tree's column
// drag is — see columnDrag.ts. A custom flavour that a browser hides during
// dragover leaves the well unable to accept the drop at all.
let cellInFlight: CellDrag | null = null

function setCellDrag(e: React.DragEvent, cell: CellDrag): void {
  cellInFlight = cell
  try {
    e.dataTransfer.effectAllowed = 'copyMove'
    e.dataTransfer.setData(CELL_MIME, JSON.stringify(cell))
    e.dataTransfer.setData('text/plain', cell.std)
  } catch {
    // No transfer object to write to — the ref above still carries the drag.
  }
}

function clearCellDrag(): void {
  cellInFlight = null
}

function hasCellDrag(e: React.DragEvent): boolean {
  try {
    if (Array.from(e.dataTransfer.types || []).includes(CELL_MIME)) return true
  } catch {
    // fall through to the ref
  }
  return cellInFlight != null
}

function readCellDrag(e: React.DragEvent): CellDrag | null {
  try {
    const raw = e.dataTransfer.getData(CELL_MIME)
    if (raw) {
      const parsed = JSON.parse(raw) as CellDrag
      if (parsed && typeof parsed.std === 'string' && typeof parsed.idx === 'number') return parsed
    }
  } catch {
    // fall through to the ref
  }
  return cellInFlight
}

/** A believable value for the preview, from the column's own declaration — a
 *  card of empty boxes says nothing about the layout being designed. */
function sampleValue(col: ColumnDef): unknown {
  const t = String(col.col_type || '').toUpperCase()
  if (t.startsWith('DATE') || t.startsWith('TIMESTAMP')) return '2026-08-12'
  if (t.startsWith('NUMBER') || t.startsWith('DECIMAL') || t.startsWith('NUMERIC')) return 486300
  const heading = col.heading || col.name || col.db_name || ''
  return heading.length > 18 ? `${heading.slice(0, 18)}…` : heading
}

function PlacedChip({
  label, heading, editHint, insertBefore, editing, draft, onDraft,
  onEdit, onCommit, onCancelEdit,
  onRemove, onDragStart, onDragEnd, onDragOver, onDrop,
}: {
  label: string
  /** The heading printed in front of the value on the card, if any. */
  heading?: string
  /** What the edit button does to THIS chip. */
  editHint: string
  /** Draw the landing line before this chip. */
  insertBefore?: boolean
  editing?: boolean
  draft: string
  onDraft: (text: string) => void
  onEdit: () => void
  onCommit: (text: string) => void
  onCancelEdit: () => void
  onRemove: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragEnd: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
}) {
  if (editing) {
    return (
      <>
        {insertBefore && <InsertLine />}
        <input
          autoFocus
          value={draft}
          onChange={(e) => onDraft(e.target.value)}
          onBlur={() => onCommit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onCommit(draft) }
            if (e.key === 'Escape') { e.preventDefault(); onCancelEdit() }
          }}
          placeholder={editHint}
          aria-label={editHint}
          style={{
            ...chipBase, width: 130, padding: '2px 6px',
            background: 'var(--color-bg)', color: 'var(--color-text)',
            borderColor: 'var(--color-accent)',
          }}
        />
      </>
    )
  }
  return (
    <>
      {insertBefore && <InsertLine />}
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
        title={`${label} — drag to reorder or move to another row`}
        style={{
          ...chipBase, cursor: 'grab', maxWidth: '100%',
          background: 'var(--color-accent-soft, rgba(37,99,235,0.08))',
          borderColor: 'var(--color-accent)', color: 'var(--color-accent)',
        }}
      >
        <GripVertical size={11} style={{ opacity: 0.55, flexShrink: 0 }} />
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {heading && (
            <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
              {heading}{' '}
            </span>
          )}
          {label}
        </span>
        <button
          type="button"
          onClick={onEdit}
          title={editHint}
          aria-label={editHint}
          style={{
            background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 1,
            color: heading ? 'inherit' : 'var(--color-text-secondary)',
          }}
        >
          <Pencil size={11} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          style={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}
        >
          <X size={11} />
        </button>
      </div>
    </>
  )
}

/** Where the drop lands — a caret between two chips on the same line. */
function InsertLine() {
  return (
    <div style={{
      width: 2, alignSelf: 'stretch', minHeight: 18, borderRadius: 2,
      background: 'var(--color-accent)',
    }} />
  )
}

const panel: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 10,
  background: 'var(--color-bg)',
}
const eyebrow: React.CSSProperties = {
  fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: 'var(--color-text-secondary)', marginBottom: 7, fontWeight: 500,
}
const well: React.CSSProperties = {
  border: '1px dashed var(--color-border)', borderRadius: 8, padding: '8px 9px',
  background: 'var(--color-bg)', minHeight: 58,
}
const wellLabel: React.CSSProperties = {
  fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: 'var(--color-text-secondary)', marginBottom: 5,
}
const chipBase: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
  fontFamily: 'var(--font-mono, monospace)',
  border: '1px solid var(--color-border)', borderRadius: 5, padding: '3px 7px',
  background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
}
const hint: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.45, marginTop: 5,
}
const select: React.CSSProperties = {
  width: '100%', fontSize: 13, padding: '4px 6px', borderRadius: 6,
  border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
}
